<section class="inventory-page">
  <header>
    <div>
      <p>Stock workspace</p>
      <h2>Inventory</h2>
      <span>Find, review and prepare purchased stock for sale.</span>
    </div>
    <div class="header-actions">
      <button type="button" class="refresh" [disabled]="isLoading" (click)="loadInventory(true)">
        <span class="material-symbols-outlined">refresh</span
        >{{ isLoading ? 'Refreshing' : 'Refresh' }}</button
      >@if (canRecordPurchase) { <a [routerLink]="portalPrefix + '/purchases'"
        ><span class="material-symbols-outlined">add</span>Record purchase</a
      > }
    </div>
  </header>
  @if (successMessage) { <p class="inventory-notice success">{{ successMessage }}</p> }

  <nav class="status-tabs" aria-label="Inventory status">
    @for (tab of tabs; track tab.value) {
      <button
        type="button"
        [class.active]="activeStatus === tab.value"
        (click)="selectTab(tab.value)"
      >
        <span class="material-symbols-outlined">{{ tab.icon }}</span
        ><span>{{ tab.label }}</span
        ><strong>{{ tabCount(tab.value) }}</strong>
      </button>
    }
  </nav>

  <section class="inventory-toolbar">
    <label class="inventory-search"
      ><span class="material-symbols-outlined">search</span
      ><input
        type="search"
        [(ngModel)]="search"
        (ngModelChange)="onSearch()"
        placeholder="Search product, model, supplier or voucher"
    /></label>
    <label
      ><span>Condition</span
      ><select [(ngModel)]="condition" (ngModelChange)="applyFilters()">
        <option value="all">All conditions</option>
        <option value="new">New</option>
        <option value="used">Used</option>
      </select></label
    >
    <label
      ><span>Sort by</span
      ><select [(ngModel)]="sort" (ngModelChange)="applyFilters()">
        <option value="newest">Newest purchase</option>
        <option value="cost-high">Cost: high to low</option>
        <option value="cost-low">Cost: low to high</option>
        <option value="stock-high">Available stock</option>
      </select></label
    >
    @if (search || condition !== 'all' || sort !== 'newest') {
      <button class="clear-filter" type="button" (click)="clearFilters()">
        <span class="material-symbols-outlined">filter_alt_off</span>Clear
      </button>
    }
  </section>

  @if (!isLoading && !errorMessage) {
    <section class="inventory-summary">
      <div>
        <span class="material-symbols-outlined">category</span>
        <p>
          <strong>{{ total }}</strong
          ><small>Matching stock lines</small>
        </p>
      </div>
      <div>
        <span class="material-symbols-outlined">deployed_code</span>
        <p>
          <strong>{{ availableUnits }} / {{ totalUnits }}</strong
          ><small>Units in loaded results</small>
        </p>
      </div>
      <div>
        <span class="material-symbols-outlined">speed</span>
        <p>
          <strong>{{ stock.length }}</strong
          ><small>Loaded now · 10 per request</small>
        </p>
      </div>
    </section>
  }
  @if (isLoading) {
    <section class="inventory-loading" aria-live="polite" aria-busy="true">
      <strong
        >Loading inventory</strong
      >
      <div class="inventory-skeleton-grid">
        @for (row of [1, 2, 3, 4, 5, 6]; track row) {
          <article class="inventory-skeleton">
            <span class="skeleton-media"></span><span class="skeleton-line short"></span
            ><span class="skeleton-line title"></span><span class="skeleton-line"></span
            ><span class="skeleton-line action"></span>
          </article>
        }
      </div>
    </section>
  }
  @if (errorMessage) {
    <div class="error">
      <span>{{ errorMessage }}</span
      ><button type="button" (click)="loadInventory(true)">Try again</button>
    </div>
  }
  @if (!isLoading && !errorMessage) {
    <div class="inventory-scroll">
      <div class="inventory-list-heading"><span>Product</span><span>Type</span><span>Purchase</span><span>Stock</span><span>Pricing</span><span>Status</span><span>Actions</span></div>
      <div class="inventory-grid">
        @for (item of stock; track item.id) {
          <article [class.sale-enabled]="item.internalSaleEnabled" [class.out-stock]="(item.availableQuantity ?? item.quantity ?? 0) === 0">
            <div class="product-media">
              @if (item.imageUrls?.length) {
                <img [src]="item.imageUrls[0]" [alt]="item.title" loading="lazy" />
              } @else {
                <span class="material-symbols-outlined">image</span>
              }
              <span
                class="stock-badge"
                [class.out]="(item.availableQuantity ?? item.quantity) === 0"
                >{{ (item.availableQuantity ?? item.quantity ?? 0) === 0 ? 'Out of stock' : (item.availableQuantity ?? item.quantity ?? 0) + ' available' }}</span
              >
            </div>
            <div class="card-top">
              <span class="condition">{{ item.condition || 'Not specified' }}</span
              ><span class="sku">{{ item.modelName || 'No model/SKU' }}</span>
            </div>
            <div class="product-copy">
              <h3>{{ item.title || 'Untitled purchased item' }}</h3>
              <p>{{ item.supplier?.name || 'Supplier unavailable' }}</p>
            </div>
            <div class="purchase-meta">
              <span
                ><span class="material-symbols-outlined">receipt_long</span
                >{{ item.purchase?.voucherNumber || 'Voucher pending' }}</span
              ><span
                ><span class="material-symbols-outlined">calendar_today</span
                >{{ purchaseDate(item.purchase?.purchasedAt || item.purchase?.createdAt) }}</span
              >
            </div>
            <div class="price-row">
              <span>Landed cost</span><strong>{{ money(landedCost(item)) }}</strong>
            </div>
            <div class="row-actions"><button class="audit-button" type="button" (click)="openAudit(item)"><span class="material-symbols-outlined">visibility</span>View details</button>@if(canManageInventory){<button class="manage-button" type="button" (click)="toggleManage(item)"><span class="material-symbols-outlined">tune</span>{{expandedItemId === item.id ? 'Close' : 'Manage'}}</button>}</div>
            @if (expandedItemId === item.id) { <div class="management-panel">
            <div class="internal-sale-state" [class.enabled]="item.internalSaleEnabled">
              <span class="material-symbols-outlined">{{ item.internalSaleEnabled ? 'point_of_sale' : 'storefront' }}</span>
              <span><strong>{{ item.internalSaleEnabled ? 'Ready for internal sale' : 'Not available in Sell screen' }}</strong><small>Marketplace publishing is optional.</small></span>
            </div>
            @if (canManageInventory) {
              <div class="discount-limit-setting pricing-setting">
                <span class="category-icon material-symbols-outlined">account_tree</span><label><span>Product subcategory</span><select [(ngModel)]="item.categoryId"><option value="">Choose product subcategory</option>@for (category of subcategories; track category.id) { <option [value]="category.id">{{ categoryPath(category) }}</option> }</select><small>The selling-price rule is applied using this exact subcategory.</small></label>
              </div>
              <div class="discount-limit-setting">
                <label><span>Maximum manual discount</span><div><input type="number" min="0" max="100" step="0.5" [(ngModel)]="item.maxDiscountPercent" /><strong>%</strong></div></label>
                <button type="button" [disabled]="updatingItemId === item.id" (click)="saveDiscountLimit(item)">Save limit</button>
              </div>
            }
            @if (canManageInventory) {
              <button class="sale-ready-button" type="button" [disabled]="updatingItemId === item.id || (item.availableQuantity ?? 0) === 0" (click)="toggleSaleReadiness(item)">
                <span class="material-symbols-outlined">{{ item.internalSaleEnabled ? 'visibility_off' : 'point_of_sale' }}</span>
                {{ updatingItemId === item.id ? 'Updating…' : item.internalSaleEnabled ? 'Remove from internal sale' : 'Publish for internal sale' }}
              </button>
            }
            @if (item.productId) {
              <div class="publish-state published">
                <span class="material-symbols-outlined">check_circle</span
                ><span
                  ><strong>Platform product linked</strong
                  ><small>Publishing is managed separately from internal sale.</small></span
                >
              </div>
            } @else {
              <div class="publish-state unpublished">
                <span class="material-symbols-outlined">pending_actions</span
                ><span
                  ><strong>Not published on platform</strong
                  ><small>Optional: complete product details and submit publicly.</small></span
                >
              </div>
              @if (canManageInventory) { <button
                class="publish-button"
                type="button"
                [disabled]="(item.availableQuantity ?? item.quantity) === 0"
                (click)="createListing(item)"
              >
                <span class="material-symbols-outlined">rocket_launch</span>Publish on platform
              </button> }
            }
            </div> }
          </article>
        } @empty {
          <div class="empty-state">
            <span class="material-symbols-outlined">inventory</span>
            <h3>No items in this view</h3>
            <p>Try another status tab or clear the current search and filters.</p>
            @if (activeStatus === 'all') {
              @if (canRecordPurchase) { <a [routerLink]="portalPrefix + '/purchases'">Record first purchase</a> }
            } @else {
              <button class="view-all-inventory" type="button" (click)="selectTab('all')">
                <span class="material-symbols-outlined" aria-hidden="true">inventory_2</span>
                <span><strong>View all inventory</strong><small>Clear this status filter</small></span>
                <span class="material-symbols-outlined arrow" aria-hidden="true">arrow_forward</span>
              </button>
            }
          </div>
        }
      </div>
      <div #loadSentinel class="load-sentinel" aria-hidden="true"></div>
      @if (isLoadingMore) {
        <div class="load-more"><span></span>Loading 10 more items…</div>
      }
      @if (!hasMore && stock.length) {
        <div class="end-state">
          <span class="material-symbols-outlined">done_all</span>All {{ total }} matching items
          loaded
        </div>
      }
    </div>
  }
</section>

@if (auditItem) {
  <div class="audit-backdrop" role="presentation" (click)="closeAudit()">
    <section class="audit-modal" role="dialog" aria-modal="true" aria-labelledby="inventory-audit-title" (click)="$event.stopPropagation()">
      <header><div><p>Inventory audit record</p><h2 id="inventory-audit-title">{{ auditItem.title }}</h2><span>{{ auditItem.purchase?.voucherNumber }}</span></div><button type="button" aria-label="Close audit" (click)="closeAudit()"><span class="material-symbols-outlined">close</span></button></header>
      <div class="audit-body">
        <section><h3>Product details</h3><dl><div><dt>Condition</dt><dd>{{ auditItem.condition }}</dd></div><div><dt>Model / SKU</dt><dd>{{ auditItem.modelName || 'Not recorded' }}</dd></div><div><dt>{{ auditItem.identifierType || 'Unique identifier' }}</dt><dd>{{ auditItem.identifierValue || 'Not recorded' }}</dd></div><div><dt>Purchase cost</dt><dd>{{ money(auditItem.unitCost) }}</dd></div><div><dt>Repair + extra</dt><dd>{{ money((auditItem.repairCost || 0) + (auditItem.extraCost || 0)) }}</dd></div><div><dt>Quantity available</dt><dd>{{ auditItem.availableQuantity }} / {{ auditItem.quantity }}</dd></div></dl><p class="audit-description">{{ auditItem.description || 'No product description recorded.' }}</p></section>
        <section><h3>Seller details</h3><dl><div><dt>Name</dt><dd>{{ auditItem.purchase?.sellerNameSnapshot || auditItem.supplier?.name || 'Not recorded' }}</dd></div><div><dt>Contact person</dt><dd>{{ auditItem.supplier?.contactName || 'Not recorded' }}</dd></div><div><dt>Phone</dt><dd>{{ auditItem.purchase?.sellerPhoneSnapshot || auditItem.supplier?.phone || 'Not recorded' }}</dd></div><div><dt>Email</dt><dd>{{ auditItem.supplier?.email || 'Not recorded' }}</dd></div><div><dt>Tax number</dt><dd>{{ auditItem.supplier?.taxNumber || 'Not recorded' }}</dd></div><div><dt>Address</dt><dd>{{ auditItem.purchase?.sellerAddressSnapshot || auditItem.supplier?.address || 'Not recorded' }}</dd></div></dl></section>
        @if (!auditItem.isSellerProductOwner) { <section><h3>Actual owner</h3><dl><div><dt>Name</dt><dd>{{ auditItem.productOwnerName }}</dd></div><div><dt>Phone</dt><dd>{{ auditItem.productOwnerPhone }}</dd></div><div><dt>Address</dt><dd>{{ auditItem.productOwnerAddress }}</dd></div></dl></section> }
        <section><h3>Product images</h3><div class="audit-evidence">@for (url of auditItem.imageUrls; track url) { <a [href]="url" target="_blank" rel="noopener"><img [src]="url" alt="Purchased product" /></a> } @empty { <span>No product images recorded.</span> }</div></section>
        <section><h3>Ownership evidence</h3><div class="audit-evidence">@for (url of evidenceUrls(auditItem.invoiceUrlsJson); track url) { <a [href]="url" target="_blank" rel="noopener"><span class="material-symbols-outlined">receipt_long</span><small>Original bill</small></a> } @for (url of evidenceUrls(auditItem.verificationImageUrlsJson); track url) { <a [href]="url" target="_blank" rel="noopener"><img [src]="url" alt="Seller with product" /><small>Seller with product</small></a> } @for (url of evidenceUrls(auditItem.productOwnerIdentityDocumentUrlsJson); track url) { <a [href]="url" target="_blank" rel="noopener"><span class="material-symbols-outlined">badge</span><small>Owner ID</small></a> } @if (!evidenceUrls(auditItem.invoiceUrlsJson).length && !evidenceUrls(auditItem.verificationImageUrlsJson).length && !evidenceUrls(auditItem.productOwnerIdentityDocumentUrlsJson).length) { <span>No evidence files recorded.</span> }</div>@if (auditItem.missingBillReason) { <p class="audit-reason"><strong>Original bill unavailable:</strong> {{ auditItem.missingBillReason }}</p> }</section>
      </div>
    </section>
  </div>
}
